import React, { useState } from 'react';
import { useIdentity } from './IdentityContext';
import { useConfirm } from './ConfirmContext';
import { useToast } from './ToastContext';
import Spinner from './Spinner';
import { ImageThumb, UploadButton, fileToBase64, validateFiles } from './ImageUploadControls';
import { GOLD_LT, CREAM } from '../theme';

export default function ImageManager({ collection, onChange }) {
  const { authFetch } = useIdentity();
  const confirm = useConfirm();
  const toast = useToast();
  const [uploadingRole, setUploadingRole] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [error, setError] = useState(null);
  const [removingId, setRemovingId] = useState(null);

  const images = collection.images || [];
  const main = images.find((img) => img.role === 'main');
  const thumb = images.find((img) => img.role === 'thumb');
  const gallery = images.filter((img) => img.role === 'gallery').sort((a, b) => a.order - b.order);

  const applyDoc = (doc) => {
    const updated = doc.collections.find((c) => c.slug === collection.slug);
    if (updated) onChange(updated);
  };

  // Uploaded one at a time (not in parallel) — each upload assigns its
  // gallery `order` based on the server's current image count for this
  // collection, so concurrent requests could race and collide on order.
  const upload = async (role, files) => {
    const list = Array.isArray(files) ? files : [files];
    setError(null);
    const valid = validateFiles(list, setError);
    if (!valid.length) return;

    setUploadingRole(role);
    try {
      for (let i = 0; i < valid.length; i++) {
        const file = valid[i];
        setUploadProgress(valid.length > 1 ? `${i + 1}/${valid.length}` : null);
        const dataBase64 = await fileToBase64(file);
        const res = await authFetch('/.netlify/functions/collections-images', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ slug: collection.slug, role, contentType: file.type, dataBase64 }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Upload failed');
        applyDoc(data.doc);
      }
      toast.success(valid.length > 1 ? 'Images uploaded.' : 'Image uploaded.');
    } catch (err) {
      setError(err.message);
      toast.error(err.message || 'Upload failed.');
    } finally {
      setUploadingRole(null);
      setUploadProgress(null);
    }
  };

  const remove = async (image) => {
    const ok = await confirm('Remove this image from the draft?');
    if (!ok) return;
    setRemovingId(image.id);
    try {
      const res = await authFetch('/.netlify/functions/collections-images', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug: collection.slug, imageId: image.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to remove image');
      applyDoc(data.doc);
      toast.success('Image removed.');
    } catch (err) {
      toast.error(err.message || 'Failed to remove image.');
    } finally {
      setRemovingId(null);
    }
  };

  const reorderGallery = async (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= gallery.length) return;
    const reordered = [...gallery];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    const withOrder = reordered.map((img, i) => ({ ...img, order: i }));
    const nextImages = [...(main ? [main] : []), ...(thumb ? [thumb] : []), ...withOrder];
    try {
      const res = await authFetch('/.netlify/functions/collections-draft', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug: collection.slug, images: nextImages }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to reorder images');
      onChange(data.collection);
    } catch (err) {
      toast.error(err.message || 'Failed to reorder images.');
    }
  };

  return (
    <div>
      <h3 style={{ fontFamily: "'Bodoni Moda', serif", fontSize: 20, margin: '0 0 16px' }}>Images</h3>
      {error && <p style={{ color: '#e08a8a', fontSize: 13 }}>{error}</p>}

      <div style={{ display: 'flex', gap: 24, marginBottom: 28, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 12, letterSpacing: '.14em', textTransform: 'uppercase', color: GOLD_LT, marginBottom: 8 }}>Main</div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
            {main && <ImageThumb image={main} size={80} />}
            <UploadButton label={main ? 'Replace' : 'Upload'} uploading={uploadingRole === 'main'} onUpload={(f) => upload('main', f)} />
          </div>
          {main && (
            <button onClick={() => remove(main)} disabled={removingId === main.id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 12, color: '#e08a8a', background: 'none', border: 'none', cursor: removingId === main.id ? 'default' : 'pointer', opacity: removingId === main.id ? .6 : 1, padding: 0 }}>
              {removingId === main.id && <Spinner size={11} />}
              {removingId === main.id ? 'Removing…' : 'Remove'}
            </button>
          )}
        </div>
        <div>
          <div style={{ fontSize: 12, letterSpacing: '.14em', textTransform: 'uppercase', color: GOLD_LT, marginBottom: 8 }}>Thumbnail</div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
            {thumb && <ImageThumb image={thumb} size={80} />}
            <UploadButton label={thumb ? 'Replace' : 'Upload'} uploading={uploadingRole === 'thumb'} onUpload={(f) => upload('thumb', f)} />
          </div>
          {thumb && (
            <button onClick={() => remove(thumb)} disabled={removingId === thumb.id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 12, color: '#e08a8a', background: 'none', border: 'none', cursor: removingId === thumb.id ? 'default' : 'pointer', opacity: removingId === thumb.id ? .6 : 1, padding: 0 }}>
              {removingId === thumb.id && <Spinner size={11} />}
              {removingId === thumb.id ? 'Removing…' : 'Remove'}
            </button>
          )}
        </div>
      </div>

      <div style={{ fontSize: 12, letterSpacing: '.14em', textTransform: 'uppercase', color: GOLD_LT, marginBottom: 8 }}>Gallery</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginBottom: 14 }}>
        {gallery.map((img, i) => (
          <div key={img.id} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <ImageThumb image={img} size={92} />
            <div style={{ display: 'flex', gap: 4, justifyContent: 'center', alignItems: 'center' }}>
              <button onClick={() => reorderGallery(i, i - 1)} disabled={i === 0} style={{ background: 'none', border: 'none', color: CREAM, cursor: i === 0 ? 'default' : 'pointer', opacity: i === 0 ? .3 : 1, fontSize: 14 }}>←</button>
              {removingId === img.id ? (
                <Spinner size={11} />
              ) : (
                <button onClick={() => remove(img)} disabled={removingId != null} style={{ background: 'none', border: 'none', color: '#e08a8a', cursor: removingId != null ? 'default' : 'pointer', opacity: removingId != null ? .4 : 1, fontSize: 14 }}>×</button>
              )}
              <button onClick={() => reorderGallery(i, i + 1)} disabled={i === gallery.length - 1} style={{ background: 'none', border: 'none', color: CREAM, cursor: i === gallery.length - 1 ? 'default' : 'pointer', opacity: i === gallery.length - 1 ? .3 : 1, fontSize: 14 }}>→</button>
            </div>
          </div>
        ))}
      </div>
      <UploadButton
        label="+ Add gallery images"
        uploading={uploadingRole === 'gallery'}
        progress={uploadProgress}
        multiple
        onUpload={(files) => upload('gallery', files)}
      />
    </div>
  );
}
